import React, { useState } from 'react';
import { Card } from '../../../design-system/Card';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Tabs } from '../../../design-system/Tabs';
import { Modal } from '../../../design-system/Modal';
import { Input } from '../../../design-system/Input';
import {
  Cpu,
  FileSpreadsheet,
  CheckCircle2,
  Plus,
  Sparkles,
  Download,
  Activity,
  Layers,
  FileCode,
  ShieldCheck
} from 'lucide-react';

export const HardwareView = () => {
  const [activeTab, setActiveTab] = useState('bom');
  const [isNewPartModalOpen, setIsNewPartModalOpen] = useState(false);
  const [isNewRevModalOpen, setIsNewRevModalOpen] = useState(false);

  // Form State
  const [partNum, setPartNum] = useState('');
  const [partDesc, setPartDesc] = useState('');
  const [partCost, setPartCost] = useState('$1.50');
  const [partSupplier, setPartSupplier] = useState('DigiKey');

  const [revName, setRevName] = useState('');
  const [revLayers, setRevLayers] = useState('4-Layer FR4');

  const tabs = [
    { id: 'trace', label: 'Requirements Traceability', icon: <Layers size={15} /> },
    { id: 'bom', label: 'Bill of Materials (BOM)', icon: <FileSpreadsheet size={15} /> },
    { id: 'pcb', label: 'PCB Revisions & DRC', icon: <Cpu size={15} /> },
    { id: 'testing', label: 'Test Plans & Validation', icon: <Activity size={15} /> },
    { id: 'firmware', label: 'Firmware & ECO', icon: <FileCode size={15} /> }
  ];

  const [bomItems, setBomItems] = useState([
    { part: 'STM32F401RET6', desc: 'ARM Cortex-M4 84MHz 512KB Flash MCU', pkg: 'LQFP-64', qty: 1, cost: 3.40, supplier: 'DigiKey', status: 'In Stock' },
    { part: 'DRV2605LDGSR', desc: 'Haptic Driver for ERM/LRA Actuators', pkg: 'VSSOP-10', qty: 2, cost: 1.85, supplier: 'Mouser', status: 'In Stock' },
    { part: 'Bourns PEC11R', desc: 'Rotary Encoder with Integrated Push Switch', pkg: 'Through-Hole', qty: 2, cost: 1.10, supplier: 'Mouser', status: 'Order Placed' },
    { part: 'USB-C 16-Pin', desc: 'SMD Receptacle Mid-Mount Connector', pkg: 'SMD', qty: 1, cost: 0.45, supplier: 'LCSC', status: 'In Stock' },
    { part: 'TPS62840DLYR', desc: 'Ultra-Low IQ 750mA Step-Down Converter', pkg: 'WSON-6', qty: 1, cost: 0.82, supplier: 'TI Direct', status: 'In Stock' }
  ]);

  const [pcbRevisions, setPcbRevisions] = useState([
    { id: 'rev_2_1', name: 'Rev 2.1 — 4-Layer High-Speed Impedance Matched', gerber: 'gerber_v2.1.3.zip', stackup: '4-Layer FR4 (1.6mm)', status: 'Passed DRC', date: 'Aug 21, 2026' },
    { id: 'rev_2_0', name: 'Rev 2.0 — Initial Engineering Prototype Prototype', gerber: 'gerber_v2.0.0.zip', stackup: '2-Layer FR4', status: 'Superseded', date: 'Aug 04, 2026' }
  ]);

  const testRuns = [
    { test: '3.3V Step-Down Ripple & Efficiency Test', condition: '5V USB-C Load @ 500mA', result: 'Pass (12mV Ripple)', engineer: 'P. Rohith', status: 'Passed' },
    { test: 'Thermal Rise under 100% MCU Load', condition: 'Ambient 25°C for 2 Hours', result: 'Pass (Max 41.2°C)', engineer: 'P. Rohith', status: 'Passed' },
    { test: 'ESD Protection Contact Discharge (IEC 61000-4-2)', condition: '±8kV on USB-C Port', result: 'Pass (No Reset)', engineer: 'Alex R.', status: 'Passed' },
    { test: 'Rotary Encoder Debounce Timing Validation', condition: '10,000 Click Actuations', result: 'Pass (< 2ms Debounce)', engineer: 'Elena R.', status: 'Passed' }
  ];

  const totalUnitCost = bomItems.reduce((acc, curr) => acc + curr.cost * curr.qty, 0).toFixed(2);

  const handleAddPart = (e) => {
    e?.preventDefault();
    if (!partNum.trim()) return;

    const parsedCost = parseFloat(partCost.replace(/[^0-9.]/g, '')) || 1.0;
    setBomItems([
      ...bomItems,
      {
        part: partNum.trim(),
        desc: partDesc.trim() || 'Electronic component',
        pkg: 'SMD',
        qty: 1,
        cost: parsedCost,
        supplier: partSupplier,
        status: 'In Stock'
      }
    ]);
    setPartNum('');
    setPartDesc('');
    setIsNewPartModalOpen(false);
  };

  const handleAddRev = (e) => {
    e?.preventDefault();
    if (!revName.trim()) return;

    setPcbRevisions([
      {
        id: 'rev_' + Date.now(),
        name: revName.trim(),
        gerber: `gerber_${revName.toLowerCase().replace(/\s+/g, '_')}.zip`,
        stackup: revLayers,
        status: 'Passed DRC',
        date: 'Today'
      },
      ...pcbRevisions
    ]);
    setRevName('');
    setIsNewRevModalOpen(false);
  };

  const handleExportBOMCSV = () => {
    const headers = ['Part Number', 'Description', 'Package', 'Qty', 'Unit Cost (USD)', 'Supplier', 'Status'];
    const rows = bomItems.map((i) => [
      `"${i.part}"`,
      `"${i.desc.replace(/"/g, '""')}"`,
      `"${i.pkg}"`,
      i.qty,
      `$${i.cost.toFixed(2)}`,
      `"${i.supplier}"`,
      i.status
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `forma_hardware_bom_v2.1_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="warning" size="sm">Hardware Engineering Methodology</Badge>
            <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Hardware & Electronics BOM Suite</h2>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Component lifecycle, part procurement, PCB stackup revisions, and validation testing
          </p>
        </div>

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* TAB 1: REQUIREMENTS TRACEABILITY */}
      {activeTab === 'trace' && (
        <Card padding="24px">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <ShieldCheck size={20} color="var(--accent-teal)" />
            <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Full Engineering Traceability Matrix</h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { req: 'REQ-01: USB-C 5V Input & Thermal Limit', design: 'TPS62840 Buck Converter', rev: 'PCB Rev 2.1', test: 'Thermal Load Test (<45°C)', status: 'Verified' },
              { req: 'REQ-02: Sub-5ms Haptic Latency', design: 'DRV2605L Haptic Driver', rev: 'PCB Rev 2.1', test: 'I2C Haptic Actuation Delay (<2ms)', status: 'Verified' },
              { req: 'REQ-03: Ultra-Low Standby Power (<50uA)', design: 'STM32 Stop Mode + TPS62840', rev: 'PCB Rev 2.1', test: 'Current Meter Test (38uA measured)', status: 'Verified' }
            ].map((trace, i) => (
              <div key={i} style={{ padding: '14px 16px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', alignItems: 'center', border: '1px solid var(--border-subtle)' }}>
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '800', color: 'var(--text-muted)' }}>REQUIREMENT</div>
                  <div style={{ fontSize: '13px', fontWeight: '700' }}>{trace.req}</div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '800', color: 'var(--text-muted)' }}>DESIGN / BOM</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{trace.design}</div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '800', color: 'var(--text-muted)' }}>TEST RUN</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{trace.test}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Badge variant="success" size="xs">
                    <CheckCircle2 size={11} style={{ marginRight: '3px' }} /> {trace.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* TAB 2: BOM */}
      {activeTab === 'bom' && (
        <Card padding="20px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Component Bill of Materials (BOM v2.1)</h3>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Estimated Unit Cost: <strong style={{ color: 'var(--accent-teal)', fontSize: '14px' }}>${totalUnitCost}</strong> per PCBA
              </p>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" size="sm" icon={<Download size={14} />} onClick={handleExportBOMCSV}>
                Export BOM (CSV)
              </Button>
              <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setIsNewPartModalOpen(true)}>
                Add Component
              </Button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-divider)', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase' }}>
                  <th style={{ padding: '10px 14px' }}>Part Number</th>
                  <th style={{ padding: '10px 14px' }}>Description</th>
                  <th style={{ padding: '10px 14px' }}>Package</th>
                  <th style={{ padding: '10px 14px' }}>Qty</th>
                  <th style={{ padding: '10px 14px' }}>Unit Cost</th>
                  <th style={{ padding: '10px 14px' }}>Supplier</th>
                  <th style={{ padding: '10px 14px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {bomItems.map((item) => (
                  <tr key={item.part} style={{ borderBottom: '1px solid var(--border-divider)' }}>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: 'var(--text-primary)' }}>{item.part}</td>
                    <td style={{ padding: '12px 14px', color: 'var(--text-secondary)' }}>{item.desc}</td>
                    <td style={{ padding: '12px 14px', color: 'var(--text-muted)' }}>{item.pkg}</td>
                    <td style={{ padding: '12px 14px', fontWeight: '600' }}>{item.qty}</td>
                    <td style={{ padding: '12px 14px', color: 'var(--accent-teal)', fontWeight: '700' }}>${item.cost.toFixed(2)}</td>
                    <td style={{ padding: '12px 14px' }}>{item.supplier}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <Badge variant={item.status === 'In Stock' ? 'success' : 'warning'} size="xs">
                        {item.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* TAB 3: PCB REVISIONS */}
      {activeTab === 'pcb' && (
        <Card padding="24px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800' }}>PCB Layout & Fabrication History</h3>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Stackup specifications, Gerber packages, and DRC rule checks</p>
            </div>
            <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setIsNewRevModalOpen(true)}>
              Register Revision
            </Button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {pcbRevisions.map((rev) => (
              <div
                key={rev.id}
                style={{
                  padding: '14px 16px',
                  backgroundColor: 'var(--surface-recessed)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '14px',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Cpu size={22} color="var(--accent-teal)" />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '800' }}>{rev.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Stackup: {rev.stackup} • Package: {rev.gerber} • {rev.date}
                    </div>
                  </div>
                </div>
                <Badge variant={rev.status === 'Passed DRC' ? 'success' : 'neutral'} size="sm">
                  {rev.status}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* TAB 4: TEST PLANS & VALIDATION */}
      {activeTab === 'testing' && (
        <Card padding="24px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Hardware Test Plan & Validation Runs</h3>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Bench measurements, ESD compliance, and thermal testing</p>
            </div>
            <Badge variant="teal" size="sm">4 / 4 Tests Passed</Badge>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {testRuns.map((tr, idx) => (
              <div
                key={idx}
                style={{
                  padding: '12px 16px',
                  backgroundColor: 'var(--surface-recessed)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700' }}>{tr.test}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Condition: {tr.condition} • Result: {tr.result} • Engineer: {tr.engineer}
                  </div>
                </div>
                <Badge variant="success" size="xs">{tr.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* TAB 5: FIRMWARE & ECO */}
      {activeTab === 'firmware' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Card padding="20px">
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '12px' }}>Embedded Firmware Builds</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ padding: '14px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '700' }}>firmware_v1.0.4.bin (FreeRTOS 10.4)</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Flash: 124KB / 512KB (24%) • RAM: 18KB / 96KB</div>
                </div>
                <Badge variant="success" size="sm">Flashed & Verified</Badge>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ADD COMPONENT MODAL */}
      <Modal
        isOpen={isNewPartModalOpen}
        onClose={() => setIsNewPartModalOpen(false)}
        title="Add BOM Component"
        subtitle="Register part number, package, and cost"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsNewPartModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAddPart} icon={<Sparkles size={14} />}>Add Component</Button>
          </>
        }
      >
        <form onSubmit={handleAddPart} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Manufacturer Part Number *"
            placeholder="e.g., TPS62840DLYR"
            value={partNum}
            onChange={(e) => setPartNum(e.target.value)}
            required
            autoFocus
          />
          <Input
            label="Component Description"
            placeholder="e.g., Step-Down Converter 750mA"
            value={partDesc}
            onChange={(e) => setPartDesc(e.target.value)}
          />
          <Input
            label="Unit Cost ($ USD)"
            placeholder="e.g., $0.85"
            value={partCost}
            onChange={(e) => setPartCost(e.target.value)}
          />
          <Input
            label="Supplier"
            placeholder="e.g., DigiKey, Mouser, LCSC"
            value={partSupplier}
            onChange={(e) => setPartSupplier(e.target.value)}
          />
        </form>
      </Modal>

      {/* REGISTER PCB REVISION MODAL */}
      <Modal
        isOpen={isNewRevModalOpen}
        onClose={() => setIsNewRevModalOpen(false)}
        title="Register PCB Revision"
        subtitle="Log layer stackup and Gerber package release"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsNewRevModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAddRev} icon={<Sparkles size={14} />}>Save Revision</Button>
          </>
        }
      >
        <form onSubmit={handleAddRev} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Revision Name *"
            placeholder="e.g., Rev 2.2 — Thermal Pad Optimization"
            value={revName}
            onChange={(e) => setRevName(e.target.value)}
            required
            autoFocus
          />
          <Input
            label="Layer Stackup"
            placeholder="e.g., 4-Layer FR4 1.6mm"
            value={revLayers}
            onChange={(e) => setRevLayers(e.target.value)}
          />
        </form>
      </Modal>
    </div>
  );
};
